import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend as RechartsLegend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { differenceInDays, format, subDays } from "date-fns";
import { apiService, API_ENDPOINTS } from "../../../config/api";

interface AlertData {
  date: string;
  totalAlerts: number;
  numParticipants: number;
}

interface AlertsSummary {
  totalAlerts: number;
  totalDays: number;
  daysWithAlerts: number;
  averageAlertsPerDay: number;
}

interface AlertsAnalyticsProps {
  studyId: string;
}

const formatDateLabel = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return format(date, "d MMM yy");
  } catch {
    return dateStr;
  }
};

const AlertsAnalytics: React.FC<AlertsAnalyticsProps> = ({ studyId }) => {
  const [data, setData] = useState<AlertData[]>([]);
  const [summary, setSummary] = useState<AlertsSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [showUsageHint, setShowUsageHint] = useState(false);
  const [startDate, setStartDate] = useState<string>(() =>
    format(subDays(new Date(), 15), "yyyy-MM-dd"),
  );
  const [endDate, setEndDate] = useState<string>(() =>
    format(new Date(), "yyyy-MM-dd"),
  );
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const validateDateRange = (start: string, end: string): boolean => {
    if (!start || !end) {
      setDateRangeError("Please select both start and end dates");
      return false;
    }

    const diff = differenceInDays(new Date(end), new Date(start));

    if (diff < 0) {
      setDateRangeError("End date must be after start date");
      return false;
    }

    setDateRangeError(null);
    return true;
  };

  const fetchAlertsData = async (start: string, end: string) => {
    if (!validateDateRange(start, end)) {
      setLoading(false);
      return;
    }

    if (!studyId) {
      setError("Missing studyId");
      setLoading(false);
      return;
    }

    try {
      setHasRun(true);
      setLoading(true);
      setError(null);

      const response = await apiService.get(
        `${API_ENDPOINTS.STUDY_ADHERENCE_MONITOR.ALERTS_ANALYTICS}?studyId=${studyId}&startDate=${start}&endDate=${end}`,
      );

      if (response.success) {
        setData(response.data || []);
        setSummary(response.summary || null);
      } else {
        setData([]);
        setSummary(null);
        setError(response.error || "Failed to load alerts analytics");
      }
    } catch (err: any) {
      setError(err.message || "Error fetching alerts analytics");
      setData([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    fetchAlertsData(startDate, endDate);
  };

  const handleResetDateRange = (days: number) => {
    const newStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    const newEnd = format(new Date(), "yyyy-MM-dd");
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  return (
    <Card sx={{ mt: 4, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              variant="h4"
              component="h3"
              sx={{ fontWeight: 600, color: "#8C1515", mb: 0 }}
            >
              Alerts Analytics
            </Typography>
            <Button
              size="small"
              variant="text"
              sx={{ minWidth: "auto", p: "4px 8px", color: "text.secondary" }}
              onClick={() => setShowUsageHint(true)}
            >
              How to use?
            </Button>
          </Box>

          <Dialog
            open={showUsageHint}
            onClose={() => setShowUsageHint(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontWeight: 600, color: "#8C1515" }}>
              How to use this graph
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" paragraph>
                  Identify days where lots of alerts are generated. High-alert
                  days may indicate systemic issues with your study.
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  Key insights:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Find days with unusual alert spikes</li>
                  <li>Identify potential technical issues</li>
                  <li>
                    Consider whether high-alert days should be excluded from
                    your analysis
                  </li>
                </Typography>
              </Box>
            </DialogContent>
          </Dialog>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Total high-priority alerts (highest EWMA score per participant per
              day) generated across the study.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ width: 200 }}
            />
            <Button
              variant="contained"
              onClick={handleApplyDateRange}
              sx={{
                mt: 1,
                backgroundColor: "#8C1515",
                "&:hover": { backgroundColor: "#6B0E0E" },
              }}
            >
              Run
            </Button>
          </Stack>
          {dateRangeError && (
            <Typography sx={{ color: "#F44336", fontSize: 12, mt: 1 }}>
              {dateRangeError}
            </Typography>
          )}
        </Box>

        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(7)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 7 Days
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(15)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 15 Days
          </Button>
          <Button
            variant="outlined"
            onClick={() => handleResetDateRange(30)}
            size="small"
            sx={{
              borderColor: "#8C1515",
              color: "#8C1515",
              "&:hover": { backgroundColor: "#FEF7F7" },
            }}
          >
            Last 30 Days
          </Button>
        </Stack>

        {summary && !loading && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#FEF7F7" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Total Alerts
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#8C1515", mt: 0.5 }}
                >
                  {summary.totalAlerts}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Days with Alerts
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {summary.daysWithAlerts} / {summary.totalDays}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Avg Alerts/Day
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {summary.averageAlertsPerDay}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Paper sx={{ p: 2, backgroundColor: "#F5F5F5" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Date Range
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#00505C", mt: 0.5 }}
                >
                  {startDate} to {endDate}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && hasRun && error && <Alert severity="error">{error}</Alert>}

        {!loading && !hasRun ? (
          <Alert severity="info">
            Select a date range and click Run to load this module.
          </Alert>
        ) : !loading && !error && data.length > 0 ? (
          <Box sx={{ width: "100%", height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 14, fill: "#333" }}
                  angle={0}
                  textAnchor="middle"
                  height={100}
                  tickFormatter={formatDateLabel}
                />
                <YAxis
                  yAxisId="left"
                  label={{
                    value: "Total Alerts",
                    angle: -90,
                    position: "insideLeft",
                  }}
                  tick={{ fontSize: 14, fill: "#333" }}
                  width={50}
                />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    padding: 10,
                  }}
                  formatter={(value, name) => {
                    if (name === "totalAlerts") return [value, "Total Alerts"];
                    return [value, name];
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <RechartsLegend wrapperStyle={{ paddingTop: 20 }} />
                <Bar
                  yAxisId="left"
                  dataKey="totalAlerts"
                  fill="#8C1515"
                  name="Total Alerts"
                  opacity={0.8}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          !loading &&
          hasRun &&
          !error && (
            <Alert severity="info">
              No alerts found for the selected date range.
            </Alert>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default AlertsAnalytics;
